// MACHINERY V2 (Stroje) — "Pridat z katalogu" as a SHEET, below 1280 (M2.16c).
//
// Rendered truth: mobile/screenshots/18-katalog-add-sheet.png. Reference impl:
// mobile/machinery-mobile-katalog.jsx `MbKatalogAddSheet` — VALUES taken, code
// not. mobile/README.md's katalog note states the rule outright: "The desktop
// 'Pridat z katalogu' dialog becomes a full-screen sheet".
//
// The FORM is catalogAddFields.tsx, shared byte-for-byte with the desktop
// dialog; the CHROME is mobile/SheetV2.tsx, shared with the fleet filter sheet
// and the readiness legend. This file is only the arrangement: subtitle, footer,
// and the readiness callout the board draws under the fields.
//
// ============================================================
//  RULE (d) — THIS SHEET HOLDS TYPED WORK AND MUST NOT EAT IT
// ============================================================
// A typed serial, a typed year and two selections. THREE separate things
// guarantee they survive a failed submit, and all three already existed:
//   1. `useCatalogAddForm.onSubmit` awaits `createMachine(...).unwrap()` and
//      calls `onCreated` — the ONLY close path — inside the SUCCESS branch. Its
//      catch sets `submitError` and returns, touching no form value. (v1 closed
//      BEFORE awaiting, at MachineDialog.tsx:370, which is the bug that rule
//      exists for.)
//   2. Nothing in this file closes on submit. `onOpenChange(false)` is reachable
//      only from Zrusit, the ✕, Esc and the backdrop — all USER gestures.
//   3. `resetAll()` runs on those deliberate LEAVE paths only, never on failure.
// The failure renders as a RetryNotice inside the scrolling body whose onRetry
// re-submits the same values. Verified by reading all three at commit time.
//
// >>> WHICH IS ALSO WHY THE SHEET IS UNMOUNTED WHEN CLOSED, NOT KEPT ALIVE. <<<
// The filter sheets stay mounted so their draft survives a close; this one is
// rendered only while `addModel` is set (CatalogOverlays), because its form
// SHOULD start clean for a new model — `useCatalogAddForm` resets on
// `model.id` change precisely so a serial typed for model A can never be
// submitted against model B.
//
// >>> THE SELECTS INSIDE IT ARE DIALOG-BORNE, AND THAT IS ALREADY HANDLED. <<<
// SheetV2 renders Overlay at 1300 / Content at 1301 — the v2 dialog tier. A menu
// opened INSIDE that belongs at 1400, and `forms/FormSelect.tsx:163-183` already
// pins itself there for exactly this case (its header records the "dropdown
// appears empty" report that came from 1290 under a 1301 dialog). The location
// select therefore needs nothing here. New menu inside this sheet? Check that
// number before assuming.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useMemo } from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { CatalogModelListItem } from "../../models/models";
import SheetV2 from "../mobile/SheetV2";
import {
  READINESS_TIER_DOT_SIZE,
  readinessTierDescriptor,
} from "../readiness/readinessTier";
import {
  deriveReadinessPresentation,
  readinessSummaryStrings,
} from "../readiness/readinessPresentation";
import CatalogAddFields from "./catalogAddFields";
import useCatalogAddForm from "./useCatalogAddForm";

export interface CatalogAddSheetProps {
  open: boolean;
  formId: string;
  model: CatalogModelListItem;
  f: ReturnType<typeof useCatalogAddForm>;
  /** Runs `resetAll()` then the caller's leave — the shared LEAVE path. */
  onLeave: (next: () => void) => void;
  onClose: () => void;
  onBack?: () => void;
}

const CatalogAddSheet: React.FC<CatalogAddSheetProps> = ({
  open,
  formId,
  model,
  f,
  onLeave,
  onClose,
  onBack,
}) => {
  const { translate } = useTranslation(["myMachinery", "common"]);

  // Derived through the ONE entry point, exactly as every other v2 surface
  // does — nothing here re-tests a raw readiness field. `ctx` is omitted
  // because a catalog MODEL has no gateway connection to report.
  //
  // MEMOISED (M2.16c review, item 6): this is the THIRD derivation for the same
  // model in one flow — the browse page derives it for the card, the popup path
  // derives it again — and unmemoised it rebuilt and re-sorted a doc-row array
  // on every keystroke in the serial field, because a controlled form re-renders
  // this whole sheet per character. Keyed on what the derivation actually reads:
  // the model identity and the translated word bank.
  const tier = useMemo(
    () =>
      readinessTierDescriptor(
        deriveReadinessPresentation(model, undefined, readinessSummaryStrings(translate))
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [model.id, translate]
  );

  return (
    <SheetV2
      open={open}
      onOpenChange={(o) => {
        if (!o) onLeave(onClose);
      }}
      title={translate("myMachinery:v2.catalog.addFromCatalog")}
      footer={
        <>
          <button
            type="button"
            className="btn"
            onClick={() => onLeave(onClose)}
            style={{
              flex: 1,
              // The Standards block's footer height (46-48). >= 44px BOTH axes;
              // flex:1 clears the width, the floor is the law.
              height: 48,
              minWidth: 44,
              justifyContent: "center",
              borderRadius: 11,
              fontSize: 13.5,
            }}
          >
            {translate("common:buttons.cancel")}
          </button>
          <button
            type="submit"
            form={formId}
            className="btn btn-mod"
            disabled={f.submitting}
            style={{
              flex: 2,
              height: 48,
              minWidth: 44,
              justifyContent: "center",
              borderRadius: 11,
              fontSize: 13.5,
            }}
          >
            {translate("myMachinery:v2.add.submit")}
          </button>
        </>
      }
    >
      <div
        style={{
          fontSize: 11,
          color: "var(--muted)",
          margin: "-4px 0 10px",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {model.modelName}
      </div>

      <CatalogAddFields
        formId={formId}
        model={model}
        f={f}
        onBack={onBack ? () => onLeave(onBack) : undefined}
        stackFields
        tierNote={
          // Board 18's accent callout. The DOT and the LABEL come from the one
          // tier table; the sentence is the existing `specsInheritedNote`'s
          // sibling — what adding does — rather than a second claim about OEM
          // coverage that nothing on this screen could substantiate.
          <div
            style={{
              display: "flex",
              gap: 9,
              alignItems: "flex-start",
              marginTop: 10,
              padding: "10px 12px",
              borderRadius: 10,
              background: "var(--mod-accent-soft)",
              border: "1px solid var(--mod-accent-line)",
              minWidth: 0,
            }}
          >
            <span
              aria-hidden
              style={{
                width: READINESS_TIER_DOT_SIZE,
                height: READINESS_TIER_DOT_SIZE,
                borderRadius: 999,
                background: tier.ink,
                flexShrink: 0,
                marginTop: 4,
                display: "inline-block",
              }}
            />
            <div style={{ fontSize: 11.5, color: "var(--ink-2)", lineHeight: 1.45, minWidth: 0 }}>
              <b>{translate(tier.labelKey)}</b>
              {" — "}
              {translate("myMachinery:v2.catalog.addTierNote")}
            </div>
          </div>
        }
      />
    </SheetV2>
  );
};

export default CatalogAddSheet;
